import { useCallback, useMemo, useState } from 'react';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import EmptyState from '@sayyesschool/frontend/components/empty-state';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import PageSection from '@sayyesschool/frontend/components/page-section';
import { useSetting } from '@sayyesschool/frontend/features/settings';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { IconButton, Surface, Table } from '@sayyesschool/frontend/ui-components';

import { contentToFormValue } from './helpers';
import TestQuestionForm from './TestQuestionForm';

function getTestData(test) {
    return {
        ...(test || {}),
        questions: test?.questions || []
    };
}

function getNextQuestionId(questions = []) {
    return questions.reduce((maxId, question) => Math.max(maxId, Number(question.id) || 0), 0) + 1;
}

function stripHtml(html = '') {
    return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncate(text = '', length = 120) {
    if (text.length <= length) return text;

    return `${text.slice(0, length).trim()}...`;
}

const columns = [
    { key: 'id', content: 'ID' },
    { key: 'topic', content: 'Тема' },
    { key: 'level', content: 'Уровень' },
    { key: 'content', content: 'Вопрос' },
    { key: 'correct', content: 'Правильный ответ' },
    { key: 'actions' }
];

export default function TestSettings() {
    const [testSetting, setTest] = useSetting('test');
    const [question, setQuestion] = useState();
    const [isFormOpen, toggleFormOpen] = useBoolean(false);
    const [isConfirmDialogOpen, toggleConfirmDialogOpen] = useBoolean(false);

    const test = useMemo(() => getTestData(testSetting), [testSetting]);

    const saveQuestions = useCallback(questions => {
        return setTest({
            ...test,
            questions
        });
    }, [setTest, test]);

    const handleCreate = useCallback(() => {
        setQuestion();
        toggleFormOpen(true);
    }, [toggleFormOpen]);

    const handleEdit = useCallback(question => {
        setQuestion(question);
        toggleFormOpen(true);
    }, [toggleFormOpen]);

    const handleDeleteClick = useCallback(question => {
        setQuestion(question);
        toggleConfirmDialogOpen(true);
    }, [toggleConfirmDialogOpen]);

    const handleCreateQuestion = useCallback(data => {
        return saveQuestions([
            ...test.questions,
            {
                ...data,
                id: getNextQuestionId(test.questions)
            }
        ]).then(() => {
            setQuestion();
            toggleFormOpen(false);
        });
    }, [saveQuestions, test.questions, toggleFormOpen]);

    const handleUpdateQuestion = useCallback(data => {
        return saveQuestions(
            test.questions.map(item => item.id === question.id ? {
                ...question,
                ...data
            } : item)
        ).then(() => {
            setQuestion();
            toggleFormOpen(false);
        });
    }, [question, saveQuestions, test.questions, toggleFormOpen]);

    const handleDeleteQuestion = useCallback(() => {
        return saveQuestions(
            test.questions.filter(item => item.id !== question.id)
        ).then(() => {
            setQuestion();
            toggleConfirmDialogOpen(false);
        });
    }, [question, saveQuestions, test.questions, toggleConfirmDialogOpen]);

    if (testSetting === undefined) return <LoadingIndicator />;

    return (
        <PageSection
            title="Тест"
            description="Создание и редактирование вопросов для теста"
            actions={[{
                key: 'add',
                icon: 'add',
                content: 'Добавить вопрос',
                variant: 'solid',
                color: 'primary',
                onClick: handleCreate
            }]}
            plain
            compact
        >
            {test.questions.length ? (
                <Surface variant="outlined">
                    <Table>
                        <Table.Head>
                            <Table.Row header>
                                {columns.map(column => (
                                    <Table.Cell
                                        key={column.key}
                                        content={column.content}
                                        header
                                    />
                                ))}
                            </Table.Row>
                        </Table.Head>

                        <Table.Body>
                            {test.questions.map(question => {
                                const correctOption = question.options?.find(option => option.correct);

                                return (
                                    <Table.Row key={question.id}>
                                        <Table.Cell content={question.id} />
                                        <Table.Cell content={question.topic} />
                                        <Table.Cell content={question.level} />
                                        <Table.Cell content={truncate(stripHtml(contentToFormValue(question.content)))} />
                                        <Table.Cell content={correctOption?.content || '-'} />

                                        <Table.Cell align="end">
                                            <IconButton.Group
                                                buttons={[
                                                    {
                                                        key: 'edit',
                                                        icon: 'edit',
                                                        title: 'Изменить',
                                                        onClick: () => handleEdit(question)
                                                    },
                                                    {
                                                        key: 'delete',
                                                        icon: 'delete',
                                                        title: 'Удалить',
                                                        color: 'danger',
                                                        onClick: () => handleDeleteClick(question)
                                                    }
                                                ]}
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </Surface>
            ) : (
                <EmptyState
                    icon="quiz"
                    title="Вопросов пока нет"
                    description="Добавьте первый вопрос для теста."
                />
            )}

            <FormDialog
                title={question ? 'Редактирование вопроса' : 'Новый вопрос'}
                open={isFormOpen}
                onClose={toggleFormOpen}
            >
                <TestQuestionForm
                    id="test-question-form"
                    question={question}
                    onSubmit={question ? handleUpdateQuestion : handleCreateQuestion}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message="Вы действительно хотите удалить вопрос?"
                open={isConfirmDialogOpen}
                onConfirm={handleDeleteQuestion}
                onClose={toggleConfirmDialogOpen}
            />
        </PageSection>
    );
}